import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { fetchWrapper } from "@helpers/fetchwrapper";
import SearchIcon from "@areas/shared/components/icons/Search";
import { ReactComponent as RunSearchIcon } from "@content/img/shared/home/svg/map-search-run-icon.svg";
import {
  loadFavoriteCompanyIds,
  toggleFavoriteCompany,
} from "@areas/fschool/helpers/companyFavoritesStorage";

import styles from "./styles/republicCompanies.module.css";

const YANDEX_MAPS_API_KEY = "7e9247c8-4c61-409a-91c5-c49872daf93e";
const MAP_CENTER = [55.958736, 54.735152];
const MAP_ZOOM = 7;
const CLUSTER_GRID_SIZE = 64;
const MARKER_SOURCE = "republic-companies-source";
const SEARCH_DEBOUNCE_MS = 350;

const FILTER_ALL = "all";
const FILTER_INDUSTRY = "industry";
const FILTER_PROFESSION = "profession";
const FILTER_FAVORITES = "favorites";

let ymapsLoadPromise = null;
let clustererCdnRegistered = false;

function loadYmaps3(apiKey) {
  if (window.ymaps3) {
    return window.ymaps3.ready.then(() => window.ymaps3);
  }

  if (!ymapsLoadPromise) {
    ymapsLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`;
      script.async = true;
      script.onload = () => {
        window.ymaps3.ready.then(() => resolve(window.ymaps3));
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return ymapsLoadPromise;
}

async function loadClusterer(ymaps3) {
  if (!clustererCdnRegistered) {
    ymaps3.import.registerCdn("https://cdn.jsdelivr.net/npm/{package}", [
      "@yandex/ymaps3-clusterer@0.0",
    ]);
    clustererCdnRegistered = true;
  }

  return ymaps3.import("@yandex/ymaps3-clusterer");
}

function enrichCoordinatesWithSpecialistNeeds(coordinates, companiesData) {
  const specialistNeedsByCompanyId = new Map();

  companiesData?.forEach((company) => {
    specialistNeedsByCompanyId.set(company.Id, company.SpecialistNeeds || []);
  });

  return coordinates.map((group) => ({
    ...group,
    Coordinates: group.Coordinates?.map((point) => ({
      ...point,
      Companies: point.Companies?.map((company) => ({
        ...company,
        SpecialistNeeds: specialistNeedsByCompanyId.get(company.Id) || [],
      })),
    })),
  }));
}

function pointHasSpecialistNeeds(point) {
  return point.Companies?.some((company) => company.SpecialistNeeds?.length > 0);
}

function formatSalaryRange(salaryMin, salaryMax) {
  const format = (value) =>
    value != null ? `${Math.round(value).toLocaleString("ru-RU")} ₽` : null;

  const min = format(salaryMin);
  const max = format(salaryMax);

  if (min && max) {
    return `${min} — ${max}`;
  }

  return min || max || "";
}

function companyMatchesQuery(company, query) {
  const name = company.Name?.toLowerCase() || "";
  const address = company.Address?.toLowerCase() || "";
  return name.includes(query) || address.includes(query);
}

function filterCoordinatesByQuery(coordinates, query) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return coordinates;
  }

  return coordinates
    .map((group) => ({
      ...group,
      Coordinates: group.Coordinates?.map((point) => {
        const matchedCompanies = point.Companies?.filter((company) =>
          companyMatchesQuery(company, normalized)
        );

        if (!matchedCompanies?.length) {
          return null;
        }

        return {
          ...point,
          Companies: matchedCompanies,
        };
      }).filter(Boolean),
    }))
    .filter((group) => group.Coordinates?.length > 0);
}

function splitFilterLabels(value) {
  return String(value || "")
    .split(/[\r\n]+/)
    .flatMap((line) => line.split(/[,;]+/))
    .map((part) =>
      part.replace(/^\d+(?:\/\d+)?[A-Za-zА-Яа-яЁё]?\)\s*/u, "").trim()
    )
    .filter(
      (part) =>
        part.length >= 3 &&
        !/^\d+(?:\/\d+)?[A-Za-zА-Яа-яЁё]?\)$/u.test(part)
    );
}

function labelsIncludeValue(source, filterValue) {
  if (!source || !filterValue) {
    return false;
  }
  if (source === filterValue) {
    return true;
  }
  return splitFilterLabels(source).some(
    (part) => part.toLowerCase() === filterValue.toLowerCase()
  );
}

function companyMatchesMarkerFilter(company, filterMode, filterValue, favoriteIds) {
  if (filterMode === FILTER_ALL) {
    return true;
  }

  if (filterMode === FILTER_FAVORITES) {
    return favoriteIds.includes(Number(company.Id));
  }

  if (!filterValue) {
    return true;
  }

  const needs = company.SpecialistNeeds || [];

  if (filterMode === FILTER_INDUSTRY) {
    return needs.some((need) => labelsIncludeValue(need.Industry, filterValue));
  }

  if (filterMode === FILTER_PROFESSION) {
    return needs.some((need) => labelsIncludeValue(need.Specialist, filterValue));
  }

  return true;
}

function filterCoordinatesByMarkerFilter(
  coordinates,
  filterMode,
  filterValue,
  favoriteIds
) {
  if (
    filterMode === FILTER_ALL ||
    ((filterMode === FILTER_INDUSTRY || filterMode === FILTER_PROFESSION) &&
      !filterValue)
  ) {
    return coordinates;
  }

  return coordinates
    .map((group) => ({
      ...group,
      Coordinates: group.Coordinates?.map((point) => {
        const matchedCompanies = point.Companies?.filter((company) =>
          companyMatchesMarkerFilter(
            company,
            filterMode,
            filterValue,
            favoriteIds
          )
        );

        if (!matchedCompanies?.length) {
          return null;
        }

        return {
          ...point,
          Companies: matchedCompanies,
        };
      }).filter(Boolean),
    }))
    .filter((group) => group.Coordinates?.length > 0);
}

function collectFilterOptions(coordinates) {
  const industries = new Set();
  const professions = new Set();

  coordinates.forEach((group) => {
    group.Coordinates?.forEach((point) => {
      point.Companies?.forEach((company) => {
        company.SpecialistNeeds?.forEach((need) => {
          splitFilterLabels(need.Industry).forEach((label) => {
            industries.add(label);
          });
          splitFilterLabels(need.Specialist).forEach((label) => {
            professions.add(label);
          });
        });
      });
    });
  });

  return {
    industries: Array.from(industries).sort((a, b) => a.localeCompare(b, "ru")),
    professions: Array.from(professions).sort((a, b) =>
      a.localeCompare(b, "ru")
    ),
  };
}

function flattenCompaniesFromCoordinates(coordinates) {
  const companies = [];
  const seen = new Set();

  coordinates.forEach((group) => {
    group.Coordinates?.forEach((point) => {
      point.Companies?.forEach((company) => {
        if (seen.has(company.Id)) {
          return;
        }
        seen.add(company.Id);
        companies.push({ company, point });
      });
    });
  });

  return companies;
}

function flattenToFeatures(coordinates) {
  const features = [];
  let id = 0;

  coordinates.forEach((group) => {
    group.Coordinates?.forEach((point) => {
      if (point.GeoLon == null || point.GeoLat == null) {
        return;
      }

      features.push({
        type: "Feature",
        id,
        geometry: {
          coordinates: [point.GeoLon, point.GeoLat],
        },
        properties: {
          point,
          companiesCount: point.Companies?.length || 1,
          hasSpecialistNeeds: pointHasSpecialistNeeds(point),
        },
      });
      id += 1;
    });
  });

  return features;
}

function getClusterCompaniesCount(features) {
  return features.reduce(
    (sum, feature) => sum + (feature.properties.companiesCount || 1),
    0
  );
}

function getBoundsFromCoordinates(coordinatesList) {
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  coordinatesList.forEach(([lon, lat]) => {
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ];
}

function createMarkerElement(count, markerClass, countClass) {
  const el = document.createElement("div");
  el.className = markerClass;

  if (count > 1) {
    const countEl = document.createElement("span");
    countEl.className = countClass;
    countEl.textContent = String(count);
    el.appendChild(countEl);
  }

  return el;
}

function createClusterElement(count, clusterClass, countClass) {
  const el = document.createElement("div");
  el.className = clusterClass;

  const countEl = document.createElement("span");
  countEl.className = countClass;
  countEl.textContent = String(count);
  el.appendChild(countEl);

  return el;
}

function FilterDropdown({
  value,
  options,
  emptyLabel,
  ariaLabel,
  onChange,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedLabel = value || emptyLabel;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div className={styles.filterDropdown} ref={rootRef}>
      <button
        type="button"
        className={`${styles.filterDropdownTrigger} ${
          open ? styles.filterDropdownTriggerOpen : ""
        }`}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={styles.filterDropdownValue}>{selectedLabel}</span>
        <span className={styles.filterDropdownChevron} aria-hidden="true" />
      </button>

      {open ? (
        <ul className={styles.filterDropdownList} role="listbox" aria-label={ariaLabel}>
          <li role="option" aria-selected={!value}>
            <button
              type="button"
              className={`${styles.filterDropdownOption} ${
                !value ? styles.filterDropdownOptionActive : ""
              }`}
              onClick={() => handleSelect("")}
            >
              {emptyLabel}
            </button>
          </li>
          {options.map((option) => (
            <li key={option} role="option" aria-selected={value === option}>
              <button
                type="button"
                className={`${styles.filterDropdownOption} ${
                  value === option ? styles.filterDropdownOptionActive : ""
                }`}
                onClick={() => handleSelect(option)}
                title={option}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function RepublicCompanies() {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const clustererRef = useRef(null);
  const mapApiRef = useRef(null);
  const onSelectPointRef = useRef(null);
  const allCoordinatesRef = useRef([]);
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const requestIdRef = useRef(0);

  const [coordinates, setCoordinates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [filterMode, setFilterMode] = useState(FILTER_ALL);
  const [industryValue, setIndustryValue] = useState("");
  const [professionValue, setProfessionValue] = useState("");
  const [favoriteIds, setFavoriteIds] = useState(() => loadFavoriteCompanyIds());
  const [filterOptions, setFilterOptions] = useState({
    industries: [],
    professions: [],
  });

  onSelectPointRef.current = setSelectedPoint;

  const isSearchActive = searchQuery.trim().length > 0;

  const filterValue =
    filterMode === FILTER_INDUSTRY
      ? industryValue
      : filterMode === FILTER_PROFESSION
        ? professionValue
        : "";

  const filteredCoordinates = useMemo(
    () =>
      filterCoordinatesByMarkerFilter(
        coordinates,
        filterMode,
        filterValue,
        favoriteIds
      ),
    [coordinates, filterMode, filterValue, favoriteIds]
  );

  const searchResults = useMemo(
    () =>
      isSearchActive
        ? flattenCompaniesFromCoordinates(filteredCoordinates)
        : [],
    [filteredCoordinates, isSearchActive]
  );

  const isFilterActive =
    filterMode === FILTER_FAVORITES ||
    ((filterMode === FILTER_INDUSTRY || filterMode === FILTER_PROFESSION) &&
      Boolean(filterValue));

  const loadCompanies = useCallback((text = "", { trackLoading = false } = {}) => {
    const requestId = ++requestIdRef.current;

    if (trackLoading) {
      setSearchLoading(true);
    }

    return fetchWrapper
      .post(
        `/v1/api/app/FindCompaniesWithPracticesAndMentorshipsAndSpecialistNeeds`,
        {
          text,
          limit: 500,
          offset: 0,
          CoordinateSize: 3,
        }
      )
      .then((res) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        const enriched = res.Coordinats?.length
          ? enrichCoordinatesWithSpecialistNeeds(res.Coordinats, res.Data)
          : [];

        if (!text.trim()) {
          allCoordinatesRef.current = enriched;
          setFilterOptions(collectFilterOptions(enriched));
        }

        setCoordinates(enriched);
      })
      .catch((error) => {
        if (requestId === requestIdRef.current) {
          console.error("RepublicCompanies load error:", error);
        }
      })
      .finally(() => {
        if (trackLoading && requestId === requestIdRef.current) {
          setSearchLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    loadCompanies("");
  }, [loadCompanies]);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return undefined;
    }

    let cancelled = false;

    const initMap = async () => {
      try {
        const ymaps3 = await loadYmaps3(YANDEX_MAPS_API_KEY);
        const { YMapClusterer, clusterByGrid } = await loadClusterer(ymaps3);

        if (cancelled || !mapContainerRef.current) {
          return;
        }

        const {
          YMap,
          YMapDefaultSchemeLayer,
          YMapFeatureDataSource,
          YMapLayer,
          YMapMarker,
        } = ymaps3;

        if (mapInstanceRef.current) {
          mapInstanceRef.current.destroy();
          mapInstanceRef.current = null;
          clustererRef.current = null;
        }

        const map = new YMap(mapContainerRef.current, {
          location: {
            center: MAP_CENTER,
            zoom: MAP_ZOOM,
          },
        });

        map
          .addChild(new YMapDefaultSchemeLayer())
          .addChild(new YMapFeatureDataSource({ id: MARKER_SOURCE }))
          .addChild(
            new YMapLayer({
              source: MARKER_SOURCE,
              type: "markers",
              zIndex: 1800,
            })
          );

        mapInstanceRef.current = map;
        mapApiRef.current = {
          YMapClusterer,
          clusterByGrid,
          YMapMarker,
        };
        setMapReady(true);
      } catch (error) {
        console.error("RepublicCompanies map init error:", error);
        setMapReady(false);
      }
    };

    initMap();

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
        clustererRef.current = null;
        mapApiRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const mapApi = mapApiRef.current;

    if (!mapReady || !map || !mapApi) {
      return undefined;
    }

    if (clustererRef.current) {
      map.removeChild(clustererRef.current);
      clustererRef.current = null;
    }

    const features = flattenToFeatures(filteredCoordinates);
    const shouldFitBounds = isSearchActive || isFilterActive;

    if (!features.length) {
      if (!shouldFitBounds) {
        map.update({
          location: {
            center: MAP_CENTER,
            zoom: MAP_ZOOM,
            duration: 300,
          },
        });
      }
      return undefined;
    }

    const { YMapClusterer, clusterByGrid, YMapMarker } = mapApi;
    const markerClass = styles.marker;
    const markerCountClass = styles.markerCount;
    const clusterClass = styles.cluster;
    const clusterCountClass = styles.clusterCount;

    const marker = (feature) => {
      const { point, companiesCount, hasSpecialistNeeds } = feature.properties;
      const markerColorClass = hasSpecialistNeeds
        ? styles.companyWithNeeds
        : styles.company;
      const markerEl = createMarkerElement(
        companiesCount,
        `${markerClass} ${markerColorClass}`,
        markerCountClass
      );
      markerEl.title =
        point.Companies?.map((company) => company.Name).join(", ") || "";

      markerEl.addEventListener("click", (e) => {
        e.stopPropagation();
        onSelectPointRef.current?.(point);
      });

      return new YMapMarker(
        {
          coordinates: feature.geometry.coordinates,
          source: MARKER_SOURCE,
        },
        markerEl
      );
    };

    const cluster = (coords, clusterFeatures) =>
      new YMapMarker(
        {
          coordinates: coords,
          source: MARKER_SOURCE,
          onClick() {
            const bounds = getBoundsFromCoordinates(
              clusterFeatures.map((feature) => feature.geometry.coordinates)
            );
            map.update({
              location: {
                bounds,
                duration: 300,
              },
            });
          },
        },
        createClusterElement(
          getClusterCompaniesCount(clusterFeatures),
          clusterClass,
          clusterCountClass
        )
      );

    const clusterer = new YMapClusterer({
      method: clusterByGrid({ gridSize: CLUSTER_GRID_SIZE }),
      features,
      marker,
      cluster,
    });

    map.addChild(clusterer);
    clustererRef.current = clusterer;

    if (shouldFitBounds) {
      if (features.length === 1) {
        map.update({
          location: {
            center: features[0].geometry.coordinates,
            zoom: 12,
            duration: 300,
          },
        });
      } else {
        map.update({
          location: {
            bounds: getBoundsFromCoordinates(
              features.map((feature) => feature.geometry.coordinates)
            ),
            duration: 300,
          },
        });
      }
    } else {
      map.update({
        location: {
          center: MAP_CENTER,
          zoom: MAP_ZOOM,
          duration: 300,
        },
      });
    }

    return () => {
      if (clustererRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeChild(clustererRef.current);
        clustererRef.current = null;
      }
    };
  }, [mapReady, filteredCoordinates, isSearchActive, isFilterActive]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedPoint) {
      return;
    }

    const stillVisible = filteredCoordinates.some((group) =>
      group.Coordinates?.some(
        (point) =>
          point.GeoLat === selectedPoint.GeoLat &&
          point.GeoLon === selectedPoint.GeoLon
      )
    );

    if (!stillVisible) {
      setSelectedPoint(null);
    }
  }, [filteredCoordinates, selectedPoint]);

  const applyClientFilter = (value) => {
    const query = value.trim();

    if (!query) {
      setCoordinates(allCoordinatesRef.current);
      setSelectedPoint(null);
      return;
    }

    setCoordinates(filterCoordinatesByQuery(allCoordinatesRef.current, query));
  };

  const scheduleApiSearch = (value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const query = value.trim();
      if (!query) {
        loadCompanies("", { trackLoading: false });
        return;
      }
      loadCompanies(query, { trackLoading: true });
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    applyClientFilter(value);
    scheduleApiSearch(value);
  };

  const handleSearchSubmit = () => {
    const value = searchInputRef.current?.value ?? searchQuery;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    setSearchQuery(value);
    applyClientFilter(value);

    const query = value.trim();
    if (!query) {
      loadCompanies("", { trackLoading: false });
      return;
    }

    loadCompanies(query, { trackLoading: true });
  };

  const handleClearSearch = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setSearchQuery("");
    setSelectedPoint(null);
    setCoordinates(allCoordinatesRef.current);
    loadCompanies("", { trackLoading: false });
  };

  const handleFilterModeChange = (mode) => {
    setFilterMode(mode);
    setSelectedPoint(null);
    if (mode !== FILTER_INDUSTRY) {
      setIndustryValue("");
    }
    if (mode !== FILTER_PROFESSION) {
      setProfessionValue("");
    }
  };

  const handleToggleFavorite = (companyId) => {
    const next = toggleFavoriteCompany(companyId);
    setFavoriteIds(next);
  };

  const handleResultClick = (point) => {
    setSelectedPoint(point);

    const map = mapInstanceRef.current;
    if (!map || point.GeoLon == null || point.GeoLat == null) {
      return;
    }

    map.update({
      location: {
        center: [point.GeoLon, point.GeoLat],
        zoom: 13,
        duration: 300,
      },
    });
  };

  const popupCompanies = useMemo(() => {
    if (!selectedPoint?.Companies?.length) {
      return [];
    }

    return selectedPoint.Companies.filter((company) =>
      companyMatchesMarkerFilter(
        company,
        filterMode,
        filterValue,
        favoriteIds
      )
    );
  }, [selectedPoint, filterMode, filterValue, favoriteIds]);

  return (
    <div className={`${styles.block} container`}>
      <div className={styles.header}>Компании республики</div>

      <div className={styles.search}>
        <SearchIcon
          style={styles.searchIcon}
          gradientId="republicCompaniesSearchIcon"
          gradientSteps={[
            { offset: 0, stopColor: "#6E9A10" },
            { offset: 1, stopColor: "#D0A024" },
          ]}
        />
        <input
          ref={searchInputRef}
          className={styles.searchInput}
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearchSubmit();
            }
          }}
          placeholder="Поиск компании"
          aria-label="Поиск компании"
        />
        {isSearchActive ? (
          <button
            type="button"
            className={styles.searchClear}
            onClick={handleClearSearch}
            aria-label="Очистить поиск"
          >
            ×
          </button>
        ) : null}
        <button
          type="button"
          className={styles.searchButton}
          onClick={handleSearchSubmit}
          aria-label="Искать"
        >
          <RunSearchIcon />
        </button>
      </div>

      <div className={styles.filters}>
        <div className={styles.filterTabs} role="tablist" aria-label="Фильтр меток">
          {[
            { id: FILTER_ALL, label: "Все" },
            { id: FILTER_INDUSTRY, label: "По отрасли" },
            { id: FILTER_PROFESSION, label: "По профессиям" },
            { id: FILTER_FAVORITES, label: "Избранное" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={filterMode === tab.id}
              className={`${styles.filterTab} ${
                filterMode === tab.id ? styles.filterTabActive : ""
              }`}
              onClick={() => handleFilterModeChange(tab.id)}
            >
              {tab.label}
              {tab.id === FILTER_FAVORITES && favoriteIds.length > 0
                ? ` (${favoriteIds.length})`
                : ""}
            </button>
          ))}
        </div>

        {filterMode === FILTER_INDUSTRY ? (
          <FilterDropdown
            value={industryValue}
            options={filterOptions.industries}
            emptyLabel="Все отрасли"
            ariaLabel="Выбор отрасли"
            onChange={(next) => {
              setIndustryValue(next);
              setSelectedPoint(null);
            }}
          />
        ) : null}

        {filterMode === FILTER_PROFESSION ? (
          <FilterDropdown
            value={professionValue}
            options={filterOptions.professions}
            emptyLabel="Все профессии"
            ariaLabel="Выбор профессии"
            onChange={(next) => {
              setProfessionValue(next);
              setSelectedPoint(null);
            }}
          />
        ) : null}

        {filterMode === FILTER_FAVORITES && favoriteIds.length === 0 ? (
          <div className={styles.filterHint}>
            Добавьте компании в избранное из карточки на карте
          </div>
        ) : null}
      </div>

      <div
        className={styles.mapWrapper}
        onClick={() => setSelectedPoint(null)}
      >
        <div ref={mapContainerRef} className={styles.map} />
        {selectedPoint && mapReady && popupCompanies.length > 0 ? (
          <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.popupClose}
              onClick={() => setSelectedPoint(null)}
              aria-label="Закрыть"
            >
              ×
            </button>
            {popupCompanies.map((comp) => {
              const isFavorite = favoriteIds.includes(Number(comp.Id));

              return (
                <div key={comp.Id} className={styles.popupItem}>
                  <div className={styles.popupTitle}>{comp.Name}</div>
                  {comp.Address ? (
                    <div className={styles.popupAddress}>{comp.Address}</div>
                  ) : null}
                  {comp.SpecialistNeeds?.length ? (
                    <div className={styles.popupSpecialists}>
                      <div className={styles.popupSpecialistsTitle}>
                        Требуемые специалисты
                      </div>
                      {comp.SpecialistNeeds.map((need) => {
                        const salary = formatSalaryRange(
                          need.SalaryMin,
                          need.SalaryMax
                        );

                        return (
                          <div key={need.Id} className={styles.popupNeed}>
                            <div className={styles.popupNeedSpecialist}>
                              {need.Specialist}
                            </div>
                            {need.Industry ? (
                              <div className={styles.popupNeedIndustry}>
                                {need.Industry}
                              </div>
                            ) : null}
                            {salary ? (
                              <div className={styles.popupNeedSalary}>
                                {salary}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  <div className={styles.popupActions}>
                    <Link
                      to={`/company/${comp.Slug}`}
                      className={styles.popupDetails}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Подробнее
                    </Link>
                    <button
                      type="button"
                      className={`${styles.popupFavorite} ${
                        isFavorite ? styles.popupFavoriteActive : ""
                      }`}
                      onClick={() => handleToggleFavorite(comp.Id)}
                    >
                      {isFavorite ? "В избранном" : "Добавить в избранное"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {isSearchActive ? (
        <div className={styles.results}>
          <div className={styles.resultsHeader}>
            <span>
              {searchLoading
                ? "Ищем компании…"
                : `Найдено: ${searchResults.length}`}
            </span>
          </div>

          {!searchLoading && searchResults.length === 0 ? (
            <div className={styles.resultsEmpty}>Ничего не найдено</div>
          ) : (
            <div className={styles.resultsList}>
              {searchResults.map(({ company, point }) => {
                const isFavorite = favoriteIds.includes(Number(company.Id));

                return (
                  <div key={company.Id} className={styles.resultCard}>
                    <button
                      type="button"
                      className={styles.resultFocus}
                      onClick={() => handleResultClick(point)}
                    >
                      <div className={styles.resultTitle}>{company.Name}</div>
                      {company.Address ? (
                        <div className={styles.resultAddress}>
                          {company.Address}
                        </div>
                      ) : null}
                    </button>

                    {company.SpecialistNeeds?.length ? (
                      <div className={styles.resultSpecialists}>
                        <div className={styles.resultSpecialistsTitle}>
                          Требуемые специалисты
                        </div>
                        {company.SpecialistNeeds.map((need) => {
                          const salary = formatSalaryRange(
                            need.SalaryMin,
                            need.SalaryMax
                          );

                          return (
                            <div key={need.Id} className={styles.resultNeed}>
                              <div className={styles.resultNeedSpecialist}>
                                {need.Specialist}
                              </div>
                              {need.Industry ? (
                                <div className={styles.resultNeedIndustry}>
                                  {need.Industry}
                                </div>
                              ) : null}
                              {salary ? (
                                <div className={styles.resultNeedSalary}>
                                  {salary}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    <div className={styles.resultActions}>
                      <Link
                        to={`/company/${company.Slug}`}
                        className={styles.resultLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Подробнее
                      </Link>
                      <button
                        type="button"
                        className={`${styles.resultFavorite} ${
                          isFavorite ? styles.resultFavoriteActive : ""
                        }`}
                        onClick={() => handleToggleFavorite(company.Id)}
                      >
                        {isFavorite ? "В избранном" : "В избранное"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default RepublicCompanies;
